import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Badge, Ratio, Spinner, Button } from 'react-bootstrap';
import { FaVideo, FaBookOpen, FaClock, FaExclamationCircle, FaChevronRight, FaTerminal } from 'react-icons/fa';
import api from '../services/api';

const NewsFeed = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                setLoading(true);
                const res = await api.get('/api/public/news');
                
                const data = res.data.news || res.data; 
                setNews(Array.isArray(data) ? data : []);
                setError(null);
            } catch (err) { 
                console.error("Lỗi tải tin tức:", err); 
                setError("Không thể kết nối với máy chủ dữ liệu.");
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, []);

    const getYouTubeID = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    if (loading) return (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{height: '60vh', backgroundColor: 'transparent'}}>
            <Spinner animation="grow" variant="warning" />
            <p className="mt-3 text-warning font-mono animate-pulse">SYSTEM_LOADING_KNOWLEDGE...</p>
        </div>
    );

    return (
        <div className="news-feed-wrapper" style={{ backgroundColor: '#020617', minHeight: '100vh', color: '#fff', paddingBottom: '50px' }}>
            <Container className="py-5">
                
                {/* HEADER PHONG CÁCH HOME */}
                <div className="tech-header-container text-center mb-5">
                    <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <span className="tech-dot"></span>
                        <span className="tech-line"></span>
                        <Badge bg="none" className="text-warning font-mono small fw-normal" style={{letterSpacing: '4px'}}>
                            KNOWLEDGE_BASE_V2
                        </Badge>
                        <span className="tech-line"></span>
                        <span className="tech-dot"></span>
                    </div>
                    <h2 className="fw-black text-gold-highlight uppercase font-main tracking-tighter mb-3" 
                        style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', lineHeight: '1.4', paddingTop: '10px' }}>
                        HƯỚNG DẪN & KIẾN THỨC
                    </h2>
                    <div className="tech-line mx-auto" style={{ width: '100px', height: '2px' }}></div>
                </div>

                {error && (
                    <div className="text-center p-4 rounded-4 border border-danger border-opacity-25 bg-danger bg-opacity-10 text-danger mb-5 font-mono">
                        <FaExclamationCircle className="me-2" /> {error}
                    </div>
                )}

                {news.length === 0 && !error ? (
                    <div className="text-center py-5 rounded-4 border border-warning border-opacity-10 bg-glass shadow-lg">
                        <FaTerminal size={40} className="text-secondary mb-3 opacity-20" />
                        <p className="text-secondary font-mono">Chưa có dữ liệu bài viết được khởi tạo trong hệ thống.</p>
                    </div>
                ) : (
                    <Row className="g-4">
                        {news.map((item) => (
                            <Col md={6} lg={4} key={item._id}>
                                <Card className="h-100 news-card glow-gold">
                                    <div className="card-media-wrapper position-relative">
                                        {item.category === 'video' || (item.type === 'video' && item.videoUrl) ? (
                                            <Ratio aspectRatio="16x9">
                                                <iframe 
                                                    src={`https://www.youtube.com/embed/${getYouTubeID(item.videoUrl)}?rel=0&modestbranding=1`}
                                                    title={item.title || item.name}
                                                    allowFullScreen
                                                    className="rounded-top-4"
                                                ></iframe>
                                            </Ratio>
                                        ) : (
                                            <div className="bg-glass-dark d-flex align-items-center justify-content-center rounded-top-4" style={{height: '200px'}}>
                                                 <FaBookOpen size={40} className="text-warning opacity-20" />
                                            </div>
                                        )}
                                        <Badge className="position-absolute top-0 start-0 m-3 px-3 py-2 btn-luxury-gold text-dark fw-bold border-0 shadow" style={{fontSize: '0.65rem', borderRadius: '8px'}}>
                                            {item.category === 'video' ? <FaVideo className="me-1"/> : <FaBookOpen className="me-1"/>}
                                            {(item.category || 'TIN TỨC').toUpperCase()}
                                        </Badge>
                                    </div>

                                    <Card.Body className="d-flex flex-column p-4 bg-glass-card rounded-bottom-4">
                                        <div className="d-flex align-items-center mb-3 text-white-50 font-mono" style={{fontSize: '0.75rem'}}>
                                            <FaClock className="me-2 text-warning"/>
                                            {new Date(item.createdAt).toLocaleDateString('vi-VN')}
                                        </div>

                                        <Card.Title className="fw-bold text-white font-main mb-3" style={{ fontSize: '1.25rem', lineHeight: '1.4', minHeight: '3.5rem' }}>
                                            {item.title || item.name}
                                        </Card.Title>

                                        <Card.Text className="small text-secondary mb-4 flex-grow-1" style={{ lineHeight: '1.6', opacity: 0.8 }}>
                                            {item.content && item.content.length > 110 
                                                ? item.content.substring(0, 110) + "..." 
                                                : item.content}
                                        </Card.Text>
                                        
                                        <Button variant="outline-warning" className="btn-tech-outline w-100 py-2 font-mono">
                                            READ_MORE <FaChevronRight className="ms-2" size={10}/>
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            <style>{`
                .bg-glass { background: rgba(255, 255, 255, 0.02); backdrop-filter: blur(10px); }
                .bg-glass-dark { background: rgba(0, 0, 0, 0.3); }
                .bg-glass-card { background: #0b1221; border-top: 1px solid rgba(255,255,255,0.05); }

                /* Tái sử dụng style từ Home */
                .text-gold-highlight { 
                    background: linear-gradient(135deg, #eab308 0%, #fef3c7 50%, #eab308 100%); 
                    -webkit-background-clip: text; 
                    -webkit-text-fill-color: transparent; 
                    background-clip: text;
                    display: block;
                }

                .btn-luxury-gold { background: linear-gradient(135deg, #eab308 0%, #fbbf24 100%) !important; }

                .news-card {
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 1.5rem;
                    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    overflow: visible;
                }

                .glow-gold:hover { 
                    border-color: rgba(234, 179, 8, 0.3) !important;
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(234, 179, 8, 0.05);
                    transform: translateY(-8px);
                }

                .btn-tech-outline {
                    border: 1px solid rgba(234, 179, 8, 0.3);
                    color: #eab308;
                    font-size: 0.8rem;
                    letter-spacing: 1px;
                    transition: 0.3s;
                    border-radius: 10px;
                }

                .btn-tech-outline:hover {
                    background: rgba(234, 179, 8, 0.1);
                    border-color: #eab308;
                    color: #fff;
                }

                .tech-line { 
                    width: 40px; 
                    height: 1px; 
                    background: linear-gradient(90deg, transparent, rgba(234, 179, 8, 0.5), transparent); 
                }

                .tech-dot { 
                    width: 4px; 
                    height: 4px; 
                    background: #eab308; 
                    border-radius: 50%; 
                    box-shadow: 0 0 10px #eab308; 
                }

                .font-mono { font-family: 'Fira Code', monospace !important; }
                .font-main { font-family: 'Inter', sans-serif !important; }
                .uppercase { text-transform: uppercase; }
                .fw-black { font-weight: 900; }

                .animate-pulse { animation: pulse 2s infinite; }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
            `}</style>
        </div>
    );
};

export default NewsFeed;